/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Box, Flex, Link, Table, Tabs, Text, TextField, Tooltip } from '@radix-ui/themes';
import { useCurrentStateAndParams, useRouter } from '@uirouter/react';
import { FileCheck, Search } from 'lucide-react';

import { EmptyState } from '../../../../shared/EmptyState';
import { ErrorState } from '../../../../shared/ErrorState';
import { FilterSection, FilterSidebar } from '../../../../shared/FilterSidebar';
import { LoadingState } from '../../../../shared/LoadingState';
import { SortableTableHeader } from '../../../../shared/SortableTableHeader/SortableTableHeader';
import type { SortDirection } from '../../../../shared/SortableTableHeader/SortableTableHeader';
import { TablePagination } from '../../../../shared/TablePagination/TablePagination';
import { ThreatLevelBadge } from '../../../../shared/ThreatLevelBadge';
import { NO_VALUE, scopeLabel, toDisplayDate, toIsoDate, truncateText } from './waiverFormat';
import { useWaiversList } from './useWaiversList';
import type { WaiverFilterId, WaiverRow, WaiverType, WaiverView, WaiversListState } from './waiversListTypes';
import { parseWaiversUrl, serializeWaiversUrl } from './waiversListUrl';

import './WaiversLivePage.scss';

const FILTERS: { id: WaiverFilterId; label: string }[] = [
  { id: 'status', label: 'Status' },
  { id: 'threat', label: 'Threat' },
  { id: 'policy', label: 'Policy' },
  { id: 'repository', label: 'Repository' },
];

const SORTABLE_COLUMNS: { key: string; label: string }[] = [
  { key: 'threat', label: 'Threat' },
  { key: 'dateCreated', label: 'Created' },
  { key: 'expiration', label: 'Expiration' },
  { key: 'policy', label: 'Policy' },
  { key: 'scope', label: 'Scope' },
];

const REQUEST_STATUS_COLOR: Record<string, 'blue' | 'green' | 'red'> = {
  requested: 'blue',
  approved: 'green',
  rejected: 'red',
};

const SEARCH_DEBOUNCE_MS = 300;

const ALL_COMPONENTS = 'All components';

const DETAIL_STATE = 'preview.security.waiverDetail';

const TABS_STORAGE_KEY = 'nexus.preview.security.waivers.tabs';

/** The tabs last shown, kept for the browser session so a reload or a refresh that drops the URL query returns to them. */
function readStoredTabs(): { type?: string; view?: string } {
  try {
    return JSON.parse(window.sessionStorage.getItem(TABS_STORAGE_KEY) ?? '{}') ?? {};
  } catch {
    return {};
  }
}

function storeTabs(type: string, view: string): void {
  try {
    window.sessionStorage.setItem(TABS_STORAGE_KEY, JSON.stringify({ type, view }));
  } catch {
    // Storage can be unavailable; the URL still carries the tabs
  }
}

/** The list's own query string, so the detail page can link back to the same page of results. */
function backParams(state: WaiversListState): string {
  const query = new URLSearchParams();
  Object.entries(serializeWaiversUrl(state)).forEach(([key, value]) => {
    if (value !== undefined) {
      query.set(key, value);
    }
  });
  return query.toString();
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** Green while the waiver is in force, red once it expired, gray when it never expires. */
function expirationColor(row: WaiverRow): 'green' | 'red' | 'gray' {
  if (row.expiryTime === null) {
    return 'gray';
  }
  return row.status === 'expired' ? 'red' : 'green';
}

interface WaiverRowsProps {
  rows: WaiverRow[];
  /** Present only where rows open the detail page. */
  detailParams?: (row: WaiverRow) => Record<string, string>;
  /** Requests show their status where waivers show the expiration. */
  requests?: boolean;
  /** Container lists cap the scope and image text, showing the full value on hover. */
  containers?: boolean;
}

/** Shows `text` as is, or cut with an ellipsis and the full text in a tooltip when `truncate` applies. */
function MaybeTruncated({ text, truncate }: { text: string; truncate?: boolean }): JSX.Element {
  const shown = truncate ? truncateText(text) : text;
  if (shown === text) {
    return <>{text}</>;
  }
  return (
    <Tooltip content={text}>
      <span data-testid="waiver-truncated-text">
        {shown}
      </span>
    </Tooltip>
  );
}

interface ComponentsLinkProps {
  text: string;
  truncate?: boolean;
  href: string;
  onOpen: () => void;
}

/** Module-level so its identity is stable across renders; otherwise each render remounts the link and drops focus. */
function ComponentsLink({ text, truncate, href, onOpen }: ComponentsLinkProps): JSX.Element {
  const shown = truncate ? truncateText(text) : text;
  const link = (
    <Link
      size="2"
      href={href}
      data-analytics-id="nxrm-security-waivers-row-open"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpen();
      }}
    >
      {shown}
    </Link>
  );
  // The tooltip wraps the link, which is the focusable element, so keyboard users get the full text too
  return shown === text ? link : <Tooltip content={text}>{link}</Tooltip>;
}

function WaiverRows({ rows, detailParams, requests, containers }: WaiverRowsProps): JSX.Element {
  const router = useRouter();
  // A container request always covers the whole image, which IQ lists as "All components"
  const allComponents = requests && containers;
  const componentsText = (row: WaiverRow) => (allComponents ? ALL_COMPONENTS : row.displayName || NO_VALUE);

  const open = (row: WaiverRow) => detailParams && router.stateService.go(DETAIL_STATE, detailParams(row));

  return (
    <>
      {rows.map((row) => (
        <Table.Row
          key={row.id}
          data-testid={`waiver-row-${row.id}`}
          onClick={detailParams ? () => open(row) : undefined}
          className={detailParams ? 'waivers-row--clickable' : undefined}
        >
          <Table.Cell>
            <Tooltip content={`Threat level ${row.threatLevel} of 10`}>
              <span>
                <ThreatLevelBadge threatLevel={row.threatLevel} />
              </span>
            </Tooltip>
          </Table.Cell>
          <Table.Cell>
            <Text size="2">{toDisplayDate(row.createTime)}</Text>
          </Table.Cell>
          {requests ? (
            <Table.Cell>
              <Text size="2">{row.requestedBy || NO_VALUE}</Text>
            </Table.Cell>
          ) : (
            <Table.Cell>
              <Badge color={expirationColor(row)} variant="soft" size="1">
                {row.expiryTime === null ? 'Never' : toIsoDate(row.expiryTime)}
              </Badge>
            </Table.Cell>
          )}
          <Table.Cell>
            <Text size="2">{row.policyName || NO_VALUE}</Text>
          </Table.Cell>
          <Table.Cell>
            <Text size="2">
              <MaybeTruncated text={scopeLabel(row.scopeOwnerType, row.scopeOwnerName)} truncate={containers} />
            </Text>
          </Table.Cell>
          <Table.Cell>
            {detailParams ? (
              <ComponentsLink
                text={componentsText(row)}
                truncate={containers}
                href={router.stateService.href(DETAIL_STATE, detailParams(row))}
                onOpen={() => open(row)}
              />
            ) : (
              <Text size="2">
                <MaybeTruncated text={componentsText(row)} truncate={containers} />
              </Text>
            )}
          </Table.Cell>
          {requests && (
            <Table.Cell>
              <Badge color={REQUEST_STATUS_COLOR[row.status?.toLowerCase() ?? ''] ?? 'gray'} variant="soft" size="1">
                {row.status ? capitalize(row.status) : NO_VALUE}
              </Badge>
            </Table.Cell>
          )}
        </Table.Row>
      ))}
    </>
  );
}

export function WaiversLivePage(): JSX.Element {
  const router = useRouter();
  const { params } = useCurrentStateAndParams();
  const state = useMemo(() => parseWaiversUrl(params), [params]);
  const { data, loading, error, reload } = useWaiversList(state);
  const [search, setSearch] = useState(state.q);
  const update = useCallback(
    (patch: Partial<WaiversListState>, resetPage = true) => {
      const next = { ...state, ...patch, page: resetPage ? 1 : (patch.page ?? state.page) };
      router.stateService.go('.', serializeWaiversUrl(next), { location: 'replace' });
    },
    [router, state]
  );

  // True from the moment a stored tab is being restored until the URL shows it
  const restoring = useRef(false);
  useEffect(() => {
    const { type, view } = readStoredTabs();
    if (params.type == null && params.view == null && (type || view)) {
      const restored = parseWaiversUrl({ type, view });
      if (restored.type !== state.type || restored.view !== state.view) {
        restoring.current = true;
        update({ type: restored.type, view: restored.view }, false);
      }
    }
    // Only on first load: afterwards the URL is the source of truth
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (restoring.current && state.type === 'components' && state.view === 'existing') {
      return;
    }
    restoring.current = false;
    storeTabs(state.type, state.view);
  }, [state.type, state.view]);

  useEffect(() => setSearch(state.q), [state.q]);
  useEffect(() => {
    if (search === state.q) {
      return undefined;
    }
    const timer = setTimeout(() => update({ q: search }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, state.q, update]);

  // Container image waivers are owned by the image's application, never by a repository.
  const filters = state.type === 'containers' ? FILTERS.filter(({ id }) => id !== 'repository') : FILTERS;

  const sections: FilterSection[] = filters.map(({ id, label }) => {
    const counts = data?.counts?.[id] ?? {};
    const values = new Set([...Object.keys(counts), ...state[id]]);
    return {
      id,
      label,
      type: 'checkbox',
      value: state[id],
      options: [...values].sort().map((value) => ({
        value,
        label: id === 'status' || id === 'threat' ? capitalize(value) : value,
        count: counts[value] ?? 0,
      })),
    };
  });

  const clearFilters = () => update({ status: [], threat: [], policy: [], repository: [], q: '' });
  const requests = state.view === 'requested';
  const columns = requests
    ? SORTABLE_COLUMNS.flatMap((column) =>
        column.key === 'dateCreated'
          ? [{ ...column, label: 'Date requested' }, { key: 'requester', label: 'Requester' }]
          : column.key === 'expiration'
            ? []
            : [column]
      )
    : SORTABLE_COLUMNS;
  const total = data?.total ?? 0;
  const hasFilters = filters.some(({ id }) => state[id].length > 0) || !!state.q;

  const back = backParams(state);
  const { type, view } = serializeWaiversUrl(state);
  const detailParams = (row: WaiverRow): Record<string, string> => ({
    waiverId: row.id,
    back,
    ...(type ? { type } : {}),
    ...(view ? { view } : {}),
  });
  const onSort = (key: string, direction: SortDirection) =>
    update({ sort: key, dir: direction === 'asc' ? 'asc' : 'desc' });

  let body: JSX.Element;
  if (error) {
    body = <ErrorState title="Waivers unavailable" message={error} onRetry={reload} retryText="Retry" />;
  } else if (loading && !data) {
    body = <LoadingState />;
  } else if (!data || total === 0) {
    body = (
      <EmptyState
        icon={FileCheck}
        title={hasFilters ? 'No waivers match the filters' : requests ? 'No waiver requests' : 'No waivers'}
        description={
          hasFilters
            ? 'Try removing a filter or changing the search.'
            : 'Waivers for this instance, including those inherited from the organization, will appear here.'
        }
      />
    );
  } else {
    body = (
      <>
        <Table.Root variant="surface" data-testid="waivers-table">
          <Table.Header>
            <Table.Row>
              {columns.map(({ key, label }) =>
                key === 'requester' ? (
                  <Table.ColumnHeaderCell key={key}>{label}</Table.ColumnHeaderCell>
                ) : (
                  <SortableTableHeader
                    key={key}
                    sortKey={key}
                    currentSortKey={state.sort}
                    currentSortDirection={state.sort ? state.dir : null}
                    onSort={onSort}
                  >
                    {label}
                  </SortableTableHeader>
                )
              )}
              <Table.ColumnHeaderCell>
                {state.type === 'containers' && !requests ? 'Images' : 'Components'}
              </Table.ColumnHeaderCell>
              {requests && <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <WaiverRows
              rows={data.results}
              detailParams={detailParams}
              requests={requests}
              containers={state.type === 'containers'}
            />
          </Table.Body>
        </Table.Root>
        <TablePagination
          currentPage={state.page}
          totalPages={Math.max(1, Math.ceil(total / state.pageSize))}
          itemsPerPage={state.pageSize}
          totalItems={total}
          onPageChange={(page) => update({ page }, false)}
          onItemsPerPageChange={(pageSize) => update({ pageSize })}
        />
      </>
    );
  }

  return (
    <Flex direction="column" gap="3" data-testid="waivers-live-page">
      <Tabs.Root value={state.type} onValueChange={(type) => update({ type: type as WaiverType, status: [], threat: [], policy: [], repository: [], q: '', sort: null })}>
        <Tabs.List>
          <Tabs.Trigger value="components" data-analytics-id="nxrm-security-waivers-tab-components-tab">
            Components
          </Tabs.Trigger>
          <Tabs.Trigger value="containers" data-analytics-id="nxrm-security-waivers-tab-containers-tab">
            Containers
          </Tabs.Trigger>
        </Tabs.List>
      </Tabs.Root>
      <Tabs.Root value={state.view} onValueChange={(view) => update({ view: view as WaiverView, status: [] })}>
        <Tabs.List size="1">
          <Tabs.Trigger value="existing" data-analytics-id="nxrm-security-waivers-view-existing-tab">
            Existing
          </Tabs.Trigger>
          <Tabs.Trigger value="requested" data-analytics-id="nxrm-security-waivers-view-requested-tab">
            Requested
          </Tabs.Trigger>
        </Tabs.List>
      </Tabs.Root>
      <Flex gap="4" align="start">
        <Box data-analytics-id="nxrm-security-waivers-filters">
          <FilterSidebar
            sections={sections}
            collapsible={false}
            onFilterChange={(id, value) => update({ [id]: Array.isArray(value) ? value : [value] })}
            onClear={clearFilters}
            disabled={!!error}
          />
        </Box>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <TextField.Root
            mb="3"
            placeholder="Search waivers"
            aria-label="Search waivers"
            data-analytics-id="nxrm-security-waivers-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          >
            <TextField.Slot>
              <Search size={14} aria-hidden />
            </TextField.Slot>
          </TextField.Root>
          {body}
        </Box>
      </Flex>
    </Flex>
  );
}
